import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// Per-tab column visibility saved to the user's account (ui_prefs.columns_{tab}).
// Stores the HIDDEN keys so newly-added columns default to visible. Columns flagged
// `defaultHidden` are stored the other way round, as "+key" when the user shows them.
export function useColumnPrefs(tabKey, columns) {
  const prefKey = `columns_${tabKey}`;
  const cacheKey = `sos-cols-${tabKey}`;
  const [hidden, setHidden] = useState(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const v = JSON.parse(raw);
        if (Array.isArray(v)) return v;
      }
    } catch { /* ignore corrupt cache */ }
    return [];
  });

  useEffect(() => {
    let live = true;
    api.get("/me/prefs").then(({ data }) => {
      if (!live) return;
      const v = data?.prefs?.[prefKey];
      if (Array.isArray(v)) {
        setHidden(v);
        try { localStorage.setItem(cacheKey, JSON.stringify(v)); } catch { /* quota */ }
      }
    }).catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefKey]);

  const persist = (next) => {
    setHidden(next);
    try { localStorage.setItem(cacheKey, JSON.stringify(next)); } catch { /* quota */ }
    api.put("/me/prefs", { key: prefKey, value: next }).catch(() => {});
  };

  const lockedKeys = useMemo(() => new Set(columns.filter((c) => c.locked).map((c) => c.key)), [columns]);
  const defaultHiddenKeys = useMemo(() => new Set(columns.filter((c) => c.defaultHidden).map((c) => c.key)), [columns]);
  const hiddenSet = useMemo(() => {
    const stored = new Set(hidden);
    return new Set(columns
      .filter((c) => !lockedKeys.has(c.key))
      .filter((c) => (defaultHiddenKeys.has(c.key) ? !stored.has(`+${c.key}`) : stored.has(c.key)))
      .map((c) => c.key));
  }, [hidden, columns, lockedKeys, defaultHiddenKeys]);
  const vis = (key) => !hiddenSet.has(key);
  const toggleColumn = (key) => {
    if (lockedKeys.has(key)) return;
    const token = defaultHiddenKeys.has(key) ? `+${key}` : key;
    const without = hidden.filter((k) => k !== token);
    // default-visible: presence = hidden; default-hidden: presence of "+key" = shown
    const nowHidden = hiddenSet.has(key);
    const shouldStore = defaultHiddenKeys.has(key) ? nowHidden : !nowHidden;
    persist(shouldStore ? [...without, token] : without);
  };
  const resetColumns = () => persist([]);

  return { vis, hiddenSet, toggleColumn, resetColumns };
}
